import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Alert,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Sync as SyncIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  Warning as WarningIcon,
  AccountBalance as BankIcon,
  TrendingUp as BalanceIcon,
  Add as AddIcon
} from '@mui/icons-material';

// Types
interface BankAccount {
  id: string;
  bank_name: string;
  account_name: string;
  account_number: string;
  sort_code: string;
  account_type: 'CURRENT' | 'SAVINGS' | 'BUSINESS';
  balance: number;
  balance_formatted: string;
  currency: string;
  status: 'CONNECTED' | 'DISCONNECTED' | 'PENDING';
  last_sync: string;
  provider: string;
  logo_url?: string;
}

interface BankAccountListProps {
  bankAccounts: BankAccount[];
  onRefresh: () => void;
  loading: boolean;
}

const BankAccountList: React.FC<BankAccountListProps> = ({
  bankAccounts,
  onRefresh,
  loading
}) => {
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Add Bank Account Dialog State
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    account_name: '',
    bank_name: '',
    account_type: 'CURRENT',
    sort_code: '',
    account_number: '',
    iban: '',
    currency: 'GBP',
    is_primary: false,
    opening_balance: 0,
    overdraft_limit: 0
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, account: BankAccount) => {
    setAnchorEl(event.currentTarget);
    setSelectedAccount(account);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAccount(null);
  };

  const handleSyncAccount = async (accountId: string) => {
    try {
      setSyncing(accountId);

      const response = await fetch(`/api/v1/banking/accounts/${accountId}/sync`, {
        method: 'POST'
      });

      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error syncing account:', error);
    } finally {
      setSyncing(null);
    }
    handleMenuClose();
  };

  // Form handlers
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.account_name.trim()) errors.account_name = 'Account name is required';
    if (!formData.bank_name.trim()) errors.bank_name = 'Bank name is required';
    if (!formData.sort_code.trim()) errors.sort_code = 'Sort code is required';
    if (!formData.account_number.trim()) errors.account_number = 'Account number is required';

    // UK Sort Code validation (6 digits, can have hyphens)
    const sortCodeRegex = /^\d{2}-?\d{2}-?\d{2}$/;
    if (formData.sort_code && !sortCodeRegex.test(formData.sort_code.replace(/\s/g, ''))) {
      errors.sort_code = 'Sort code must be 6 digits (e.g., 12-34-56)';
    }

    // UK Account Number validation (exactly 8 digits)
    const accountNumberRegex = /^\d{8}$/;
    if (formData.account_number && !accountNumberRegex.test(formData.account_number.replace(/\s/g, ''))) {
      errors.account_number = 'Account number must be exactly 8 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const requestData = {
        ...formData,
        sort_code: formData.sort_code.replace(/\s|-/g, '').replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3'),
        account_number: formData.account_number.replace(/\s/g, ''),
        opening_balance: parseFloat(formData.opening_balance.toString()) || 0,
        overdraft_limit: parseFloat(formData.overdraft_limit.toString()) || 0
      };

      console.log('Sending bank account data:', requestData);

      const response = await fetch('/api/v1/banking/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        setShowAddDialog(false);
        setFormData({
          account_name: '',
          bank_name: '',
          account_type: 'CURRENT',
          sort_code: '',
          account_number: '',
          iban: '',
          currency: 'GBP',
          is_primary: false,
          opening_balance: 0,
          overdraft_limit: 0
        });
        onRefresh();
      } else {
        const errorData = await response.json();
        console.error('Validation error:', errorData);

        // Handle validation errors
        if (errorData.detail && Array.isArray(errorData.detail)) {
          const validationErrors: { [key: string]: string } = {};
          errorData.detail.forEach((error: any) => {
            if (error.loc && error.loc.length > 1) {
              const field = error.loc[1];
              validationErrors[field] = error.msg;
            }
          });
          setFormErrors(validationErrors);
        } else {
          alert(`Error: ${errorData.detail || 'Failed to add bank account'}`);
        }
      }
    } catch (error) {
      console.error('Error adding bank account:', error);
      alert('Failed to add bank account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONNECTED': return 'success';
      case 'DISCONNECTED': return 'error';
      case 'PENDING': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONNECTED': return <ConnectedIcon />;
      case 'DISCONNECTED': return <DisconnectedIcon />;
      case 'PENDING': return <WarningIcon />;
      default: return <BankIcon />;
    }
  };

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case 'BUSINESS': return 'primary';
      case 'CURRENT': return 'info';
      case 'SAVINGS': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Loading bank accounts...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (bankAccounts.length === 0) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            <strong>No bank accounts connected</strong>
          </Typography>
          <Typography variant="body2">
            Add your UK bank account details to start managing your finances and importing transactions.
          </Typography>
        </Alert>

        <Box sx={{ textAlign: 'center', py: 4 }}>
          <BankIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Get Started with Banking
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Add your first bank account to begin tracking transactions and managing your finances.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
          >
            Add Your First Bank Account
          </Button>
        </Box>

        {/* Add Bank Account Dialog */}
        <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <BankIcon />
              Add Bank Account
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Account Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    value={formData.account_name}
                    onChange={(e) => handleInputChange('account_name', e.target.value)}
                    error={!!formErrors.account_name}
                    helperText={formErrors.account_name || 'e.g., Business Current Account'}
                    placeholder="Business Current Account"
                  />
                </Grid>

                {/* Bank Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    error={!!formErrors.bank_name}
                    helperText={formErrors.bank_name || 'e.g., Barclays, HSBC, Lloyds'}
                    placeholder="Barclays"
                  />
                </Grid>

                {/* Account Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={formData.account_type}
                      label="Account Type"
                      onChange={(e) => handleInputChange('account_type', e.target.value)}
                    >
                      <SelectMenuItem value="CURRENT">Current Account</SelectMenuItem>
                      <SelectMenuItem value="SAVINGS">Savings Account</SelectMenuItem>
                      <SelectMenuItem value="CREDIT">Credit Account</SelectMenuItem>
                      <SelectMenuItem value="LOAN">Loan Account</SelectMenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Currency */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.currency}
                      label="Currency"
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                    >
                      <SelectMenuItem value="GBP">GBP (£)</SelectMenuItem>
                      <SelectMenuItem value="EUR">EUR (€)</SelectMenuItem>
                      <SelectMenuItem value="USD">USD ($)</SelectMenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sort Code */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sort Code"
                    value={formData.sort_code}
                    onChange={(e) => handleInputChange('sort_code', e.target.value)}
                    error={!!formErrors.sort_code}
                    helperText={formErrors.sort_code || 'UK format: 12-34-56'}
                    placeholder="12-34-56"
                    inputProps={{ maxLength: 8 }}
                  />
                </Grid>

                {/* Account Number */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    value={formData.account_number}
                    onChange={(e) => handleInputChange('account_number', e.target.value)}
                    error={!!formErrors.account_number}
                    helperText={formErrors.account_number || '8-12 digits'}
                    placeholder="12345678"
                    inputProps={{ maxLength: 12 }}
                  />
                </Grid>

                {/* IBAN (Optional) */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="IBAN (Optional)"
                    value={formData.iban}
                    onChange={(e) => handleInputChange('iban', e.target.value)}
                    helperText="International Bank Account Number (optional)"
                    placeholder="GB29 NWBK 6016 1331 9268 19"
                  />
                </Grid>

                {/* Opening Balance */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Opening Balance"
                    type="number"
                    value={formData.opening_balance}
                    onChange={(e) => handleInputChange('opening_balance', parseFloat(e.target.value) || 0)}
                    helperText="Current account balance"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                    }}
                  />
                </Grid>

                {/* Overdraft Limit */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Overdraft Limit"
                    type="number"
                    value={formData.overdraft_limit}
                    onChange={(e) => handleInputChange('overdraft_limit', parseFloat(e.target.value) || 0)}
                    helperText="Enter 0 if no overdraft facility"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                    }}
                  />
                </Grid>

                {/* Primary Account */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_primary}
                        onChange={(e) => handleInputChange('is_primary', e.target.checked)}
                      />
                    }
                    label="Set as Primary Account"
                  />
                  <Typography variant="caption" display="block" color="textSecondary">
                    Primary account will be used as default for transactions
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Adding...' : 'Add Account'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Calculate totals
  const totalBalance = bankAccounts.reduce((sum, account) => sum + account.balance, 0);
  const connectedAccounts = bankAccounts.filter(account => account.status === 'CONNECTED').length;

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <BankIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Accounts</Typography>
                  <Typography variant="h6">{bankAccounts.length}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {connectedAccounts} connected
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <BalanceIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Balance</Typography>
                  <Typography variant="h6">
                    {totalBalance.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Across all accounts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <SyncIcon color="info" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Last Sync</Typography>
                  <Typography variant="body2">
                    {bankAccounts[0]?.last_sync
                      ? new Date(bankAccounts[0].last_sync).toLocaleString('en-GB')
                      : 'Never'
                    }
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Most recent
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Bank Account Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Bank Accounts</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddDialog(true)}
          sx={{ minWidth: 160 }}
        >
          Add Bank Account
        </Button>
      </Box>

      {/* Bank Accounts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bank & Account</TableCell>
              <TableCell>Account Details</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Sync</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bankAccounts.map((account) => (
              <TableRow key={account.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 40, height: 40 }}>
                      {account.bank_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {account.bank_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {account.account_name}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {account.sort_code} • ****{account.account_number.slice(-4)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {account.provider}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={account.account_type}
                    color={getAccountTypeColor(account.account_type) as any}
                    size="small"
                  />
                </TableCell>

                <TableCell align="right">
                  <Typography variant="body2" fontWeight="medium">
                    {account.balance_formatted}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {account.currency}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    icon={getStatusIcon(account.status)}
                    label={account.status}
                    color={getStatusColor(account.status) as any}
                    size="small"
                  />
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {account.last_sync
                      ? new Date(account.last_sync).toLocaleDateString('en-GB')
                      : 'Never'
                    }
                  </Typography>
                  {account.last_sync && (
                    <Typography variant="caption" color="textSecondary">
                      {new Date(account.last_sync).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="center">
                  {syncing === account.id ? (
                    <LinearProgress sx={{ width: 40 }} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, account)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedAccount && handleSyncAccount(selectedAccount.id)}>
          <SyncIcon sx={{ mr: 1 }} /> Sync Now
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ViewIcon sx={{ mr: 1 }} /> View Transactions
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <SettingsIcon sx={{ mr: 1 }} /> Account Settings
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Disconnect
        </MenuItem>
      </Menu>

      {/* Account Health Alerts */}
      <Box sx={{ mt: 3 }}>
        {bankAccounts.some(account => account.status === 'DISCONNECTED') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Connection Issues:</strong> Some bank accounts are disconnected.
              Reconnect them to continue importing transactions automatically.
            </Typography>
          </Alert>
        )}

        {bankAccounts.some(account => account.status === 'PENDING') && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Pending Connections:</strong> Some bank connections are still being set up.
              This may take a few minutes to complete.
            </Typography>
          </Alert>
        )}

        {bankAccounts.every(account => account.status === 'CONNECTED') && bankAccounts.length > 0 && (
          <Alert severity="success">
            <Typography variant="body2">
              <strong>All Connected:</strong> All your bank accounts are successfully connected and syncing automatically.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Add Bank Account Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BankIcon />
            Add Bank Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {/* Account Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={formData.account_name}
                  onChange={(e) => handleInputChange('account_name', e.target.value)}
                  error={!!formErrors.account_name}
                  helperText={formErrors.account_name || 'e.g., Business Current Account'}
                  placeholder="Business Current Account"
                />
              </Grid>

              {/* Bank Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={formData.bank_name}
                  onChange={(e) => handleInputChange('bank_name', e.target.value)}
                  error={!!formErrors.bank_name}
                  helperText={formErrors.bank_name || 'e.g., Barclays, HSBC, Lloyds'}
                  placeholder="Barclays"
                />
              </Grid>

              {/* Account Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={formData.account_type}
                    label="Account Type"
                    onChange={(e) => handleInputChange('account_type', e.target.value)}
                  >
                    <SelectMenuItem value="CURRENT">Current Account</SelectMenuItem>
                    <SelectMenuItem value="SAVINGS">Savings Account</SelectMenuItem>
                    <SelectMenuItem value="CREDIT">Credit Account</SelectMenuItem>
                    <SelectMenuItem value="LOAN">Loan Account</SelectMenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Currency */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency}
                    label="Currency"
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                  >
                    <SelectMenuItem value="GBP">GBP (£)</SelectMenuItem>
                    <SelectMenuItem value="EUR">EUR (€)</SelectMenuItem>
                    <SelectMenuItem value="USD">USD ($)</SelectMenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Sort Code */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sort Code"
                  value={formData.sort_code}
                  onChange={(e) => handleInputChange('sort_code', e.target.value)}
                  error={!!formErrors.sort_code}
                  helperText={formErrors.sort_code || 'UK format: 12-34-56'}
                  placeholder="12-34-56"
                  inputProps={{ maxLength: 8 }}
                />
              </Grid>

              {/* Account Number */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={formData.account_number}
                  onChange={(e) => handleInputChange('account_number', e.target.value)}
                  error={!!formErrors.account_number}
                  helperText={formErrors.account_number || 'Exactly 8 digits'}
                  placeholder="12345678"
                  inputProps={{ maxLength: 8 }}
                />
              </Grid>

              {/* IBAN (Optional) */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IBAN (Optional)"
                  value={formData.iban}
                  onChange={(e) => handleInputChange('iban', e.target.value)}
                  helperText="International Bank Account Number (optional)"
                  placeholder="GB29 NWBK 6016 1331 9268 19"
                />
              </Grid>

              {/* Opening Balance */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Opening Balance"
                  type="number"
                  value={formData.opening_balance}
                  onChange={(e) => handleInputChange('opening_balance', parseFloat(e.target.value) || 0)}
                  helperText="Current account balance"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                  }}
                />
              </Grid>

              {/* Overdraft Limit */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Overdraft Limit"
                  type="number"
                  value={formData.overdraft_limit}
                  onChange={(e) => handleInputChange('overdraft_limit', parseFloat(e.target.value) || 0)}
                  helperText="Enter 0 if no overdraft facility"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                  }}
                />
              </Grid>

              {/* Primary Account */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_primary}
                      onChange={(e) => handleInputChange('is_primary', e.target.checked)}
                    />
                  }
                  label="Set as Primary Account"
                />
                <Typography variant="caption" display="block" color="textSecondary">
                  Primary account will be used as default for transactions
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankAccountList;
